import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * InactivityTracker - Monitors user activity and handles timeouts
 * 
 * Rules (configurable via admin settings):
 * - Admin/Team members: Away timeout, Logout timeout, Offline timeout
 * - Regular users: Logout timeout
 */
export function InactivityTracker({ userType, token, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const lastActivityRef = useRef(Date.now());
  const awayTimeoutRef = useRef(null);
  const offlineTimeoutRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const isAwayRef = useRef(false);
  const [timeouts, setTimeouts] = useState(null);

  // Fetch timeout settings from backend
  useEffect(() => {
    const fetchTimeoutSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/inactivity-timeouts`);
        setTimeouts(res.data);
      } catch (error) {
        console.error('Failed to fetch timeout settings, using defaults');
        // Default timeout configurations (in milliseconds)
        setTimeouts({
          agent_away_ms: 5 * 60 * 1000,      // 5 minutes
          agent_logout_ms: 15 * 60 * 1000,   // 15 minutes
          agent_offline_ms: 20 * 60 * 1000,  // 20 minutes
          user_logout_ms: 5 * 60 * 1000      // 5 minutes
        });
      }
    };
    fetchTimeoutSettings();
  }, []);

  const updateTeamStatus = useCallback(async (status) => {
    if (userType !== 'agent' || !token) return;
    
    try {
      await axios.post(`${API}/agent/activity/status`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  }, [userType, token]);

  const handleLogout = useCallback(() => {
    // Clear all timeouts
    if (awayTimeoutRef.current) clearTimeout(awayTimeoutRef.current);
    if (offlineTimeoutRef.current) clearTimeout(offlineTimeoutRef.current);
    if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);

    // Update status to offline for agents before logout
    if (userType === 'agent') {
      updateTeamStatus('offline');
    }

    // Show toast notification
    toast.warning('Session expired', {
      description: 'You have been logged out due to inactivity.'
    });

    // Call the logout handler
    if (onLogout) {
      onLogout();
    }

    // Navigate to login page
    if (userType === 'agent') {
      localStorage.removeItem('agent_token');
      navigate('/agent/login');
    } else {
      localStorage.removeItem('token');
      navigate('/login');
    }
  }, [userType, onLogout, navigate, updateTeamStatus]);

  const resetTimers = useCallback(() => {
    if (!timeouts) return; // Wait for timeout settings to load
    
    lastActivityRef.current = Date.now();

    // Clear existing timeouts
    if (awayTimeoutRef.current) clearTimeout(awayTimeoutRef.current);
    if (offlineTimeoutRef.current) clearTimeout(offlineTimeoutRef.current);
    if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);

    // If user was away, mark them as online again
    if (isAwayRef.current && userType === 'agent') {
      isAwayRef.current = false;
      updateTeamStatus('online');
    }

    if (userType === 'agent') {
      // Set away timeout
      awayTimeoutRef.current = setTimeout(() => {
        isAwayRef.current = true;
        updateTeamStatus('away');
      }, timeouts.agent_away_ms);

      // Set offline timeout
      offlineTimeoutRef.current = setTimeout(() => {
        updateTeamStatus('offline');
      }, timeouts.agent_offline_ms);

      // Set logout timeout
      logoutTimeoutRef.current = setTimeout(() => {
        handleLogout();
      }, timeouts.agent_logout_ms);
    } else {
      // Regular user logout
      logoutTimeoutRef.current = setTimeout(() => {
        handleLogout();
      }, timeouts.user_logout_ms);
    }
  }, [userType, handleLogout, updateTeamStatus, timeouts]);

  useEffect(() => {
    // Don't track on public pages
    const publicPaths = ['/', '/login', '/register', '/agent/login', '/faq', '/knowledge-base', '/about', '/privacy', '/terms', '/careers'];
    if (publicPaths.some(path => location.pathname === path || location.pathname.startsWith('/knowledge-base/'))) {
      return;
    }

    // Only track if user is authenticated and timeouts are loaded
    if (!token || !timeouts) return;

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel'
    ];

    // Throttle activity updates to prevent excessive calls
    let throttleTimer = null;
    const throttledResetTimers = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        resetTimers();
        throttleTimer = null;
      }, 1000); // Throttle to once per second
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledResetTimers, { passive: true });
    });

    // Initialize timers
    resetTimers();

    // Mark as online when component mounts (for agents)
    if (userType === 'agent') {
      updateTeamStatus('online');
    }

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledResetTimers);
      });
      if (awayTimeoutRef.current) clearTimeout(awayTimeoutRef.current);
      if (offlineTimeoutRef.current) clearTimeout(offlineTimeoutRef.current);
      if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [token, location.pathname, userType, resetTimers, updateTeamStatus, timeouts]);

  // Handle visibility change (tab switch, minimize)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User switched tabs or minimized - treat as potential inactivity
        if (userType === 'agent') {
          // Mark as away after a short delay when tab is hidden
          awayTimeoutRef.current = setTimeout(() => {
            isAwayRef.current = true;
            updateTeamStatus('away');
          }, 30000); // 30 seconds after tab hidden
        }
      } else {
        // User returned - reset timers and mark as online
        resetTimers();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userType, resetTimers, updateTeamStatus]);

  // Handle beforeunload - mark as offline when closing tab
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (userType === 'agent' && token) {
        // Use sendBeacon for reliable delivery on page close
        const data = JSON.stringify({ status: 'offline' });
        navigator.sendBeacon(
          `${API}/agent/activity/beacon?token=${token}`,
          data
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [userType, token]);

  return null; // This component doesn't render anything
}

export default InactivityTracker;
